/* @flow */
import { capitalize } from 'lodash';
import pluralize from 'pluralize';
import Group from 'entities/Group';

import type { OrganizationRole } from 'queries/__generated__/globalGQLTypes';
import type { GroupTargetRole } from 'entities/Message';

import StaffIcon from 'assets/images/roles/staff.svg';
import TeacherIcon from 'assets/images/roles/teacher.svg';
import AdminIcon from 'assets/images/bell.svg';
import ParentIcon from 'assets/images/roles/parent.svg';
import StudentIcon from 'assets/images/roles/student.svg';
import EveryoneIcon from 'assets/images/roles/everyone.svg';
import NoRoleIcon from 'assets/images/roles/no-role.svg';

export const STUDENT = 'student';
export const PARENT = 'parent';
export const ADMIN = 'admin';
export const TEACHER = 'teacher';
export const STAFF = 'staff';
export const UNKNOWN = 'unknown';
export const EVERYONE = 'everyone';
export const ALL = 'all';
export const RCE = 'rce';

export const ALL_ROLES = [TEACHER, STUDENT, STAFF, PARENT, ADMIN, UNKNOWN];

export const subgroupName = {
  [STUDENT]: 'Students',
  [PARENT]: 'Parents',
  [ADMIN]: 'Administrators',
  [TEACHER]: 'Teachers',
  [EVERYONE]: 'Everyone',
  [STAFF]: 'Staff',
};

export const roleName = {
  [STUDENT]: 'Students',
  [PARENT]: 'Parents',
  [ADMIN]: 'Administrators',
  [TEACHER]: 'Teachers',
  [EVERYONE]: 'Everyone',
  [UNKNOWN]: 'No role',
  [STAFF]: 'Staff',
};

export const roleNameSingular = {
  [STUDENT]: 'Student',
  [PARENT]: 'Parent',
  [ADMIN]: 'Admin',
  [TEACHER]: 'Teacher',
  [RCE]: 'Remind Connected Educator',
  [STAFF]: 'Staff',
};

export const getGroupDescription = (group: Group, role: ?string) => {
  const name = group.getName();
  if (!role || role === EVERYONE) {
    return name;
  } else {
    return `${name} ${String.fromCharCode(160) +
      String.fromCharCode(8250) +
      String.fromCharCode(160)} ${subgroupName[role]}`;
  }
};

export const getGroupKey = (
  groupId: Backbone$Id = '',
  role: GroupTargetRole = EVERYONE
): string => {
  // eslint-disable-line
  return `${groupId}${role === EVERYONE ? '' : role}`;
};

export type RoleFlags = {
  student: boolean,
  parent: boolean,
  teacher: boolean,
  admin: boolean,
  staff: boolean,
  rce: boolean,
};

export const roleToIcon = {
  [STUDENT]: StudentIcon,
  [PARENT]: ParentIcon,
  [TEACHER]: TeacherIcon,
  [ADMIN]: AdminIcon,
  [RCE]: AdminIcon,
  [STAFF]: StaffIcon,
  [EVERYONE]: EveryoneIcon,
  [UNKNOWN]: NoRoleIcon,
};

export function rolesFlags(rolesList: OrganizationRole[]): RoleFlags {
  const flags: RoleFlags = {
    student: false,
    parent: false,
    teacher: false,
    admin: false,
    rce: false,
    staff: false,
  };
  rolesList.forEach(key => {
    flags[key] = true;
  });
  return flags;
}

export function roleListFromFlags(flags: RoleFlags): OrganizationRole[] {
  return Object.keys(flags).filter(r => flags[r]);
}

export function formatRole(role: string, count?: number): ?string {
  switch (role) {
    case STAFF:
    case EVERYONE:
      return capitalize(role);
    case STUDENT:
    case TEACHER:
    case ADMIN:
    case PARENT:
      return capitalize(pluralize(role, count || 0));
    case UNKNOWN:
      return 'No role';
    case ALL:
      return 'Everyone';
    default:
      return null;
  }
}
